// /ia-report/:token — public download page for a client Internal Audit report (link in the report email, valid
// 3 months). Only ever the LATEST version: the button shows once that version is ready; until then the client is
// asked to check again (never handed an older version).
import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { FirmHeader } from '../components/PublicComponents'

// Both reports used to read just "Internal Audit Report", so a client holding the two links could not tell
// them apart until the PDF had downloaded. The suffixes match the email ("the report marked (Company) …")
// and the file names (32013-Company / 32013-Audit).
const KIND = { client: 'Internal Audit Report - Company', clean: 'Internal Audit Report - Audit', closeout: 'Internal Audit Close-out Report' }

export default function IAReportDownload() {
  const { token } = useParams()
  const [info, setInfo] = useState(null)      // get_ia_report_link result
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState('')

  function check() {
    setMsg('')
    supabase.rpc('get_ia_report_link', { p_token: token }).then(({ data }) => setInfo(data || { status: 'invalid' }))
  }
  useEffect(check, [token])

  async function download() {
    setBusy(true); setMsg('')
    try {
      const { data } = await supabase.functions.invoke('ia-public', { body: { action: 'download', token } })
      if (data?.status === 'ready' && data.url) { window.location.href = data.url }
      else if (['preparing', 'error', 'expired', 'invalid'].includes(data?.status)) setInfo(i => ({ ...i, status: data.status }))
      else setMsg('Sorry, the report could not be downloaded. Please try again.')
    } catch (e) { setMsg('Sorry, the report could not be downloaded. Please try again.') }
    setBusy(false)
  }

  const box = (children) => (
    <div style={{ minHeight: '100vh', background: '#f7f6f3', padding: '40px 20px' }}>
      <div style={{ maxWidth: 520, margin: '0 auto' }}>
        <FirmHeader accredited={false} />
        <div style={{ background: '#fff', borderRadius: 12, padding: 32, border: '0.5px solid rgba(0,0,0,0.1)', textAlign: 'center' }}>{children}</div>
      </div>
    </div>
  )

  if (!info) return box(<div style={{ color: '#999' }}>Loading…</div>)
  if (info.status === 'invalid') return box(<><h2 style={{ fontWeight: 500 }}>Invalid link</h2><p style={{ color: '#666', fontSize: 14 }}>This link is not valid. Please reply to our email or call us at (65) 6376 0550.</p></>)
  if (info.status === 'expired') return box(<><h2 style={{ fontWeight: 500 }}>This link has expired</h2><p style={{ color: '#666', fontSize: 14, lineHeight: 1.6 }}>Download links are valid for 3 months. Please reply to our email and we will send you a new link.</p></>)
  const btn = { padding: '12px 28px', borderRadius: 8, border: 'none', background: '#006363', color: '#fff', fontWeight: 600, fontSize: 15, cursor: 'pointer', fontFamily: 'inherit' }
  const notReady = info.status === 'preparing' || info.status === 'error'
  return box(<>
    <div style={{ fontSize: 13, color: '#888', marginBottom: 6 }}>{info.company_name}</div>
    <h2 style={{ fontWeight: 500, marginBottom: 18 }}>{KIND[info.kind] || 'Internal Audit Report'}</h2>
    {notReady
      ? <>
          <p style={{ color: '#666', fontSize: 14, lineHeight: 1.6, margin: '0 0 16px' }}>
            {info.status === 'preparing'
              ? 'Your report is being prepared. This usually takes a minute.'
              : 'Your report is being finalised. Please check again later, or reply to our email if you need it urgently.'}
          </p>
          <button onClick={check} style={btn}>Check again</button>
        </>
      : <button onClick={download} disabled={busy} style={btn}>{busy ? 'Preparing…' : 'Download report (PDF)'}</button>}
    {msg && <p style={{ color: '#8a5a00', fontSize: 13, marginTop: 14 }}>{msg}</p>}
    <p style={{ color: '#999', fontSize: 12, marginTop: 18 }}>This link is valid until {new Date(info.expires_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}.</p>
  </>)
}
